import type { DetectorResult } from "../core/types.js";
import type { XPost } from "../sources/x.js";
import type { HeliusTransaction } from "../sources/helius.js";

/**
 * Timing anomaly detector.
 *
 * Returns a count of anomalies rather than a 0-100 score. The aggregator
 * normalizes the count to a score (0 anomalies = 100, 10+ = 0).
 *
 * Anomalies flagged:
 *  - Sub-second response times (bot-like)
 *  - Perfect regularity in posting intervals (cron job signature)
 *  - Simultaneous X and on-chain activity within 1 second of each other
 *  - Posts during 4am-7am UTC with high frequency
 *  - Absolute silence gaps > 48h followed by burst activity
 */
export function timingDetector(
  posts: XPost[],
  txs: HeliusTransaction[]
): DetectorResult {
  const notes: string[] = [];
  let anomalies = 0;

  // 1. Sub-second responses (reply posted within 1s of parent).
  const quickReplies = posts.filter(
    (p) => p.in_reply_to_id && p.reply_delta_ms !== undefined && p.reply_delta_ms < 1000
  ).length;
  if (quickReplies > 0) {
    anomalies += Math.min(3, quickReplies);
    notes.push(`${quickReplies} sub-second reply posts`);
  }

  // 2. Perfect regularity: check if 5+ consecutive intervals have delta < 5s.
  const sortedPosts = [...posts].sort((a, b) => a.created_at - b.created_at);
  let regular = 0;
  for (let i = 2; i < sortedPosts.length; i++) {
    const d1 = sortedPosts[i]!.created_at - sortedPosts[i - 1]!.created_at;
    const d2 = sortedPosts[i - 1]!.created_at - sortedPosts[i - 2]!.created_at;
    if (Math.abs(d1 - d2) < 5 && d1 > 0) regular++;
  }
  if (regular >= 5) {
    anomalies++;
    notes.push(`${regular} posts with cron-like regularity`);
  }

  // 3. Simultaneous X + on-chain activity.
  let simultaneous = 0;
  const txTimes = new Set(txs.map((t) => t.timestamp));
  for (const p of posts) {
    for (let offset = -1; offset <= 1; offset++) {
      if (txTimes.has(p.created_at + offset)) {
        simultaneous++;
        break;
      }
    }
  }
  if (simultaneous >= 3) {
    anomalies++;
    notes.push(`${simultaneous} simultaneous X+onchain events (within 1s)`);
  }

  // 4. High frequency in 4am-7am UTC window.
  const deadHour = posts.filter((p) => {
    const h = new Date(p.created_at * 1000).getUTCHours();
    return h >= 4 && h < 7;
  }).length;
  if (deadHour / posts.length > 0.1) {
    anomalies++;
    notes.push(`${deadHour} posts during 4-7am UTC dead zone`);
  }

  // 5. Silence-then-burst patterns.
  let burstAfterSilence = 0;
  for (let i = 1; i < sortedPosts.length; i++) {
    const gap = sortedPosts[i]!.created_at - sortedPosts[i - 1]!.created_at;
    if (gap > 48 * 3600) {
      // Check if next 5 posts come within 10 minutes.
      const nextFive = sortedPosts.slice(i, i + 5);
      if (nextFive.length === 5) {
        const span = nextFive[4]!.created_at - nextFive[0]!.created_at;
        if (span < 600) burstAfterSilence++;
      }
    }
  }
  if (burstAfterSilence > 0) {
    anomalies += burstAfterSilence;
    notes.push(`${burstAfterSilence} silence-then-burst patterns`);
  }

  if (anomalies === 0) {
    notes.push("no timing anomalies detected");
  }

  return {
    detector: "timing",
    score: anomalies, // raw anomaly count, aggregator normalizes
    samples: posts.length + txs.length,
    notes,
    runtime_ms: 0,
  };
}
